"use client";

// Port of reference/scopely-frontend/src/components/ui/animated-grid-pattern.tsx
// (Magic UI) for the landing page (#396). Same props, same SVG structure,
// same motion in normal viewing — a grid of faint squares that fade in,
// fade out, and respawn at a new cell — with ONE declared divergence: the reference
// animates each square with `motion/react`; this app does not carry that
// dependency and adding a 100KB animation library for one background is
// the wrong trade. The fade is CSS (`gridSquarePulse` in globals.css) and
// the respawn rides the `animationiteration` event, which is exactly the
// moment the reference's `onAnimationComplete` fires. Decorative only:
// aria-hidden, pointer-events none. Under prefers-reduced-motion the two
// DIFFER on purpose: globals.css collapses every CSS animation to one
// near-instant iteration, so these squares rest invisible, while the
// reference's JS-driven tween keeps pulsing. The WCAG-favourable side of
// that split is the one kept here.
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from "react";

import { cn } from "./cn";

export interface AnimatedGridPatternProps extends ComponentPropsWithoutRef<"svg"> {
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  strokeDasharray?: number;
  numSquares?: number;
  maxOpacity?: number;
  duration?: number;
  repeatDelay?: number;
}

type Square = { id: number; pos: [number, number]; iteration: number };

export function AnimatedGridPattern({
  width = 40,
  height = 40,
  x = -1,
  y = -1,
  strokeDasharray = 0,
  numSquares = 50,
  className,
  maxOpacity = 0.5,
  duration = 4,
  repeatDelay = 0.5,
  style,
  ...props
}: AnimatedGridPatternProps) {
  const id = useId();
  const containerRef = useRef<SVGSVGElement | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [squareOverrides, setSquareOverrides] = useState<Record<number, Square>>({});

  const getPos = useCallback((): [number, number] => {
    return [
      Math.floor((Math.random() * dimensions.width) / width),
      Math.floor((Math.random() * dimensions.height) / height),
    ];
  }, [dimensions.height, dimensions.width, height, width]);

  const squares = useMemo(
    () =>
      Array.from({ length: numSquares }, (_, i) => {
        return squareOverrides[i] ?? { id: i, pos: getPos(), iteration: 0 };
      }),
    [getPos, numSquares, squareOverrides],
  );

  const updateSquarePosition = useCallback(
    (squareId: number) => {
      setSquareOverrides((current) => ({
        ...current,
        [squareId]: {
          id: squareId,
          pos: getPos(),
          iteration: (current[squareId]?.iteration ?? 0) + 1,
        },
      }));
    },
    [getPos],
  );

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions((current) => {
          const next = { width: entry.contentRect.width, height: entry.contentRect.height };
          return current.width === next.width && current.height === next.height ? current : next;
        });
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // The reference's motion timeline per square: fade 0 -> maxOpacity over
  // `duration`, hold `repeatDelay`, reverse — so one full pulse lasts
  // 2*duration + repeatDelay, staggered 0.1s per square.
  const pulseSeconds = duration * 2 + repeatDelay;

  return (
    <svg
      ref={containerRef}
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 h-full w-full fill-gray-400/30 stroke-gray-400/30",
        className,
      )}
      style={{ ["--grid-square-max-opacity" as string]: maxOpacity, ...style }}
      {...props}
    >
      <defs>
        <pattern id={id} width={width} height={height} patternUnits="userSpaceOnUse" x={x} y={y}>
          <path d={`M.5 ${height}V.5H${width}`} fill="none" strokeDasharray={strokeDasharray} />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
      <svg x={x} y={y} className="overflow-visible">
        {squares.map(({ pos: [squareX, squareY], id: squareId, iteration }, index) => (
          <rect
            key={`${squareId}-${iteration}`}
            className="grid-square-pulse"
            style={{
              animationDuration: `${pulseSeconds}s`,
              animationDelay: `${index * 0.1}s`,
            }}
            onAnimationIteration={() => updateSquarePosition(squareId)}
            width={width - 1}
            height={height - 1}
            x={squareX * width + 1}
            y={squareY * height + 1}
            fill="currentColor"
            strokeWidth="0"
          />
        ))}
      </svg>
    </svg>
  );
}
